/**
 * Growth Analytics Dashboard
 * Track growth metrics, signups, referrals, and path to 1000 users
 */

import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  TrendingUp,
  Users,
  UserPlus,
  Mail,
  Key,
  Target,
  Calendar,
  Award,
  Download,
  RefreshCw,
} from 'lucide-react';

interface AnalyticsData {
  totalUsers: number;
  activeUsers: number;
  waitlistCount: number;
  betaKeysIssued: number;
  betaKeysUsed: number;
  referrals: number;
  dailyCheckIns: number;
  avgRTI: number;
  streakAvg: number;
}

export default function GrowthAnalytics() {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalUsers: 0,
    activeUsers: 0,
    waitlistCount: 0,
    betaKeysIssued: 0,
    betaKeysUsed: 0,
    referrals: 0,
    dailyCheckIns: 0,
    avgRTI: 0,
    streakAvg: 0,
  });

  const [growthData, setGrowthData] = useState<{ date: string; users: number }[]>([]);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | 'all'>('7d');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = () => {
    // Load beta waitlist
    const waitlist = JSON.parse(localStorage.getItem('beta_waitlist') || '[]');
    
    // Load beta keys
    const betaKeys = JSON.parse(localStorage.getItem('beta_keys') || '[]');
    const usedKeys = betaKeys.filter((k: any) => k.usedBy).length;

    // Load user accounts (simulate from beta keys used)
    const totalUsers = usedKeys;
    
    // Calculate active users (users with check-ins in last 7 days)
    const activeUsers = calculateActiveUsers();

    // Calculate total referrals across all users
    const totalReferrals = waitlist.reduce((sum: number, entry: any) => 
      sum + (entry.referrals?.length || 0), 0
    );

    // Load check-ins data
    const checkIns = loadCheckInsData();

    setAnalytics({
      totalUsers,
      activeUsers,
      waitlistCount: waitlist.length,
      betaKeysIssued: betaKeys.length,
      betaKeysUsed: usedKeys,
      referrals: totalReferrals,
      dailyCheckIns: checkIns.today,
      avgRTI: checkIns.avgRTI,
      streakAvg: checkIns.avgStreak,
    });

    // Generate growth data
    setGrowthData(generateGrowthData(timeRange));
  };

  const calculateActiveUsers = () => {
    // Simulate active users (in production, this would query actual user activity)
    const betaKeys = JSON.parse(localStorage.getItem('beta_keys') || '[]');
    const usedKeys = betaKeys.filter((k: any) => k.usedBy).length;
    
    // Assume 60% of users are active
    return Math.round(usedKeys * 0.6);
  };

  const loadCheckInsData = () => {
    // Simulate check-in data (in production, this would aggregate from all users)
    return {
      today: Math.round(analytics.activeUsers * 0.8),
      avgRTI: 72.5,
      avgStreak: 5.3,
    };
  };

  const generateGrowthData = (range: '7d' | '30d' | 'all') => {
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
    const data = [];
    
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      // Simulate growth curve
      const baseUsers = analytics.totalUsers || 0;
      const dayProgress = (days - i) / days;
      const users = Math.round(baseUsers * dayProgress);
      
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        users,
      });
    }
    
    return data;
  };

  const exportData = () => {
    const csv = [
      ['Metric', 'Value'],
      ['Total Users', analytics.totalUsers],
      ['Active Users', analytics.activeUsers],
      ['Waitlist Count', analytics.waitlistCount],
      ['Beta Keys Issued', analytics.betaKeysIssued],
      ['Beta Keys Used', analytics.betaKeysUsed],
      ['Total Referrals', analytics.referrals],
      ['Daily Check-ins', analytics.dailyCheckIns],
      ['Average score', analytics.avgRTI],
      ['Average Streak', analytics.streakAvg],
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tension-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Progress to 1000 users
  const targetUsers = 1000;
  const progressPercent = (analytics.totalUsers / targetUsers) * 100;
  const usersRemaining = targetUsers - analytics.totalUsers;
  const conversionRate = analytics.betaKeysUsed > 0 
    ? (analytics.betaKeysUsed / analytics.betaKeysIssued * 100).toFixed(1)
    : '0';

  return (
    <div className="min-h-screen tension-page-bg pb-20">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-light">Growth Analytics</h1>
            <p className="text-sm text-neutral-600 mt-1">
              Track your path to 1,000 users
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadAnalytics}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={exportData}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Progress to 1000 */}
        <Card className="p-8 bg-gradient-to-br from-purple-600 to-pink-600 text-white">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-6 h-6" />
                <h2 className="text-2xl font-bold">Goal: 1,000 Users</h2>
              </div>
              <p className="text-purple-100">
                {usersRemaining > 0 
                  ? `${usersRemaining.toLocaleString()} users to go`
                  : '🎉 Goal achieved!'
                }
              </p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-4xl font-bold">{analytics.totalUsers}</div>
              <div className="text-xs text-purple-100 mt-1">Total Users</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Progress</span>
              <span className="font-semibold">{progressPercent.toFixed(1)}%</span>
            </div>
            <div className="h-4 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${Math.min(progressPercent, 100)}%` }}
              />
            </div>
          </div>

          {/* Milestones */}
          <div className="grid grid-cols-4 gap-3 mt-6">
            <div className={`text-center p-3 rounded-lg ${analytics.totalUsers >= 100 ? 'bg-white/30' : 'bg-white/10'}`}>
              <div className="text-2xl font-bold">100</div>
              <div className="text-xs text-purple-100">First 100</div>
            </div>
            <div className={`text-center p-3 rounded-lg ${analytics.totalUsers >= 250 ? 'bg-white/30' : 'bg-white/10'}`}>
              <div className="text-2xl font-bold">250</div>
              <div className="text-xs text-purple-100">Quarter</div>
            </div>
            <div className={`text-center p-3 rounded-lg ${analytics.totalUsers >= 500 ? 'bg-white/30' : 'bg-white/10'}`}>
              <div className="text-2xl font-bold">500</div>
              <div className="text-xs text-purple-100">Halfway</div>
            </div>
            <div className={`text-center p-3 rounded-lg ${analytics.totalUsers >= 1000 ? 'bg-white/30' : 'bg-white/10'}`}>
              <div className="text-2xl font-bold">1K</div>
              <div className="text-xs text-purple-100">Goal!</div>
            </div>
          </div>
        </Card>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <Users className="w-5 h-5 text-green-600" />
              <span className="text-xs text-neutral-500">+12% this week</span>
            </div>
            <div className="text-3xl font-bold text-neutral-900 mb-1">
              {analytics.activeUsers}
            </div>
            <div className="text-sm text-neutral-600">Active Users</div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <Mail className="w-5 h-5 text-purple-600" />
              <span className="text-xs text-neutral-500">Waitlist</span>
            </div>
            <div className="text-3xl font-bold text-neutral-900 mb-1">
              {analytics.waitlistCount}
            </div>
            <div className="text-sm text-neutral-600">On Waitlist</div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <UserPlus className="w-5 h-5 text-blue-600" />
              <span className="text-xs text-neutral-500">{conversionRate}% rate</span>
            </div>
            <div className="text-3xl font-bold text-neutral-900 mb-1">
              {analytics.betaKeysUsed}
            </div>
            <div className="text-sm text-neutral-600">Keys Activated</div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <TrendingUp className="w-5 h-5 text-orange-600" />
              <span className="text-xs text-neutral-500">Viral</span>
            </div>
            <div className="text-3xl font-bold text-neutral-900 mb-1">
              {analytics.referrals}
            </div>
            <div className="text-sm text-neutral-600">Total Referrals</div>
          </Card>
        </div>

        {/* Engagement Metrics */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-neutral-900">
                  {analytics.dailyCheckIns}
                </div>
                <div className="text-sm text-neutral-600">Check-ins Today</div>
              </div>
            </div>
            <div className="text-xs text-neutral-500">
              {analytics.activeUsers > 0 
                ? `${((analytics.dailyCheckIns / analytics.activeUsers) * 100).toFixed(0)}% daily engagement`
                : '0% daily engagement'
              }
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-neutral-900">
                  {analytics.avgRTI.toFixed(1)}
                </div>
                <div className="text-sm text-neutral-600">Average score</div>
              </div>
            </div>
            <div className="text-xs text-neutral-500">
              Across all active relationships
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-neutral-900">
                  {analytics.streakAvg.toFixed(1)}
                </div>
                <div className="text-sm text-neutral-600">Avg Streak (days)</div>
              </div>
            </div>
            <div className="text-xs text-neutral-500">
              User retention indicator
            </div>
          </Card>
        </div>

        {/* Growth Chart */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-neutral-900">User Growth</h3>
            <div className="flex gap-2">
              <Button
                variant={timeRange === '7d' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeRange('7d')}
              >
                7D
              </Button>
              <Button
                variant={timeRange === '30d' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeRange('30d')}
              >
                30D
              </Button>
              <Button
                variant={timeRange === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeRange('all')}
              >
                All
              </Button>
            </div>
          </div>

          {/* Simple Bar Chart */}
          <div className="space-y-2">
            {growthData.map((data, index) => {
              const maxUsers = Math.max(...growthData.map(d => d.users), 1);
              const width = (data.users / maxUsers) * 100;
              
              return (
                <div key={index} className="flex items-center gap-3">
                  <div className="text-xs text-neutral-500 w-16">{data.date}</div>
                  <div className="flex-1 bg-neutral-100 rounded-full h-8 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-end px-3 transition-all duration-300"
                      style={{ width: `${width}%` }}
                    >
                      {width > 20 && (
                        <span className="text-xs font-semibold text-white">
                          {data.users}
                        </span>
                      )}
                    </div>
                  </div>
                  {width <= 20 && (
                    <span className="text-xs font-semibold text-neutral-600 w-8">
                      {data.users}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Funnel Analysis */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-6">Conversion Funnel</h3>
          <div className="space-y-4">
            {/* Waitlist → Beta Key */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-neutral-700">Waitlist Signups</span>
                <span className="text-sm font-bold text-neutral-900">{analytics.waitlistCount}</span>
              </div>
              <div className="h-3 bg-neutral-200 rounded-full overflow-hidden">
                <div className="h-full bg-purple-600 rounded-full" style={{ width: '100%' }} />
              </div>
            </div>

            {/* Beta Keys Issued */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-neutral-700">Beta Keys Issued</span>
                <span className="text-sm font-bold text-neutral-900">{analytics.betaKeysIssued}</span>
              </div>
              <div className="h-3 bg-neutral-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-600 rounded-full" 
                  style={{ width: `${analytics.waitlistCount > 0 ? (analytics.betaKeysIssued / analytics.waitlistCount * 100) : 0}%` }} 
                />
              </div>
            </div>

            {/* Beta Keys Used */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-neutral-700">Keys Activated (Users)</span>
                <span className="text-sm font-bold text-neutral-900">{analytics.betaKeysUsed}</span>
              </div>
              <div className="h-3 bg-neutral-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-600 rounded-full" 
                  style={{ width: `${analytics.betaKeysIssued > 0 ? (analytics.betaKeysUsed / analytics.betaKeysIssued * 100) : 0}%` }} 
                />
              </div>
            </div>

            {/* Active Users */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-neutral-700">Active Users (3+ check-ins)</span>
                <span className="text-sm font-bold text-neutral-900">{analytics.activeUsers}</span>
              </div>
              <div className="h-3 bg-neutral-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-orange-600 rounded-full" 
                  style={{ width: `${analytics.betaKeysUsed > 0 ? (analytics.activeUsers / analytics.betaKeysUsed * 100) : 0}%` }} 
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Action Items */}
        <Card className="p-6 bg-blue-50 border-blue-200">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">🚀 Growth Action Items</h3>
          <div className="space-y-3 text-sm text-neutral-700">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                1
              </div>
              <p>
                <strong>Issue more beta keys:</strong> You have {analytics.waitlistCount} people waiting. 
                Convert top waitlist users to beta testers.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                2
              </div>
              <p>
                <strong>Activate referrals:</strong> {analytics.referrals} total referrals means viral growth is working. 
                Share analytics with top referrers.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                3
              </div>
              <p>
                <strong>Improve activation:</strong> {conversionRate}% of beta keys are being used. 
                Send reminder emails to inactive key holders.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                4
              </div>
              <p>
                <strong>Content marketing:</strong> Share anonymized insights from {analytics.totalUsers} users 
                to attract more signups.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
